// LiquiPlanungBentoTile — Phase F.2 (Sprint 7 Final-Pass).
//
// Top-3 kritische Steuer-Termine + Monats-Burn-Rate + empfohlene
// 3M-Mindest-Liquidität + (sobald EBICS aktiv) aktueller Cash. Backend
// /api/v1/kpi/liqui-planung liefert die Daten auch ohne Banking — Tile
// soll im Hero-Bereich für Kevin auf einen Blick zeigen "was muss da
// sein, was ist da".

import {
  type LiquiPlanungResponse,
  type LiquiSeverity,
  formatEUR,
} from "../../../lib/api";
import { formatEurCompact } from "../../../lib/bwa-aggregate";
import { flashClass, useFlashOnChange } from "../../../hooks/useFlashOnChange";

interface Props {
  data?: LiquiPlanungResponse | null;
  loading?: boolean;
}

const SEV_CLASS: Record<LiquiSeverity, string> = {
  overdue: "text-rose-300 border-rose-500/40",
  urgent: "text-rose-300 border-rose-500/40",
  soon: "text-amber-200 border-amber-500/40",
  scheduled: "text-slate-300 border-slate-600/40",
};

const SEV_LABEL: Record<LiquiSeverity, string> = {
  overdue: "ÜBERFÄLLIG",
  urgent: "PINK",
  soon: "AMBER",
  scheduled: "OK",
};

export default function LiquiPlanungBentoTile({ data, loading }: Props) {
  // Hook VOR den Early-Returns (Rules of Hooks): flasht, wenn sich der
  // konsolidierte Cash nach Refetch/Edit ändert.
  const cashFlash = useFlashOnChange(
    data?.aktueller_cash_konsolidiert_eur
      ? Number(data.aktueller_cash_konsolidiert_eur)
      : null,
  );

  if (loading) return <Skeleton />;

  if (!data) {
    return (
      <div className="h-full flex flex-col gap-2">
        <header className="text-[11px] uppercase tracking-widest opacity-60">
          Liquiditäts-Planung
        </header>
        <p className="text-xs text-slate-500">Lade …</p>
      </div>
    );
  }

  const termine = data.next_critical_termine;
  const burnRate = Number(data.monats_burn_rate_eur ?? "0");
  const mindest = Number(data.empfohlene_mindest_liquiditaet_eur ?? "0");
  const cash = data.aktueller_cash_konsolidiert_eur
    ? Number(data.aktueller_cash_konsolidiert_eur)
    : null;
  // Cash-Graph (K7): dieselbe verknüpfte Deckung/Reichweite wie im
  // LageHeader — eine Quelle, überall dieselbe Zahl.
  const deckung = data.deckung_pct;
  const reichweite = data.reichweite_tage;

  // Coverage-Severity: cash < mindest → critical, < 1.5x → warn, sonst ok.
  const coverage: "ok" | "warn" | "critical" | "no_cash" =
    cash === null
      ? "no_cash"
      : cash < mindest
        ? "critical"
        : cash < mindest * 1.5
          ? "warn"
          : "ok";
  const coverageClass =
    coverage === "critical"
      ? "text-rose-300"
      : coverage === "warn"
        ? "text-amber-200"
        : coverage === "ok"
          ? "text-emerald-300"
          : "text-slate-400";

  return (
    <div className="h-full flex flex-col gap-3">
      <header className="flex items-baseline justify-between gap-2">
        <span className="text-[11px] uppercase tracking-widest opacity-60">
          Liquiditäts-Planung
        </span>
        <span className="text-[10px] tabular-nums text-slate-500">
          3-Monats-Regel
        </span>
      </header>

      {/* Top-3 kritische Termine */}
      <section className="space-y-1">
        <p className="text-[10px] uppercase tracking-wider text-slate-400">
          Kritische Termine
        </p>
        {termine.length === 0 && (
          <p className="text-[11px] text-slate-500">
            Keine relevanten Termine in den nächsten 180 Tagen
          </p>
        )}
        <ul className="space-y-1">
          {termine.map((t) => (
            <li
              key={`${t.firma_id}-${t.datum}-${t.art_display}`}
              className={`flex items-baseline justify-between gap-2 text-[11px] tabular-nums border-l-2 pl-2 ${SEV_CLASS[t.severity]}`}
              title={t.headline}
            >
              <span className="flex-1 min-w-0 truncate">
                <span className="opacity-90">▶ {t.art_display}</span>{" "}
                <span className="opacity-60">{t.firma_display}</span>
              </span>
              <span className="shrink-0 font-semibold">
                {formatEUR(t.betrag_eur)}
              </span>
              <span className="shrink-0 text-[10px] opacity-70 min-w-[3rem] text-right">
                {t.days_until <= 0 ? "heute" : `in ${t.days_until} T`}
              </span>
              <span className="shrink-0 text-[9px] opacity-70 uppercase tracking-wider">
                {SEV_LABEL[t.severity]}
              </span>
            </li>
          ))}
        </ul>
      </section>

      {/* Burn-Rate + Mindest-Liquidität */}
      <section className="grid grid-cols-2 gap-2 text-[11px] mt-auto">
        <div className="rounded-lg border border-slate-700/60 bg-slate-900/40 p-2">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">
            Monats-Burn-Rate
          </div>
          <div className="text-base font-semibold text-slate-100 tabular-nums">
            ~ {formatEurCompact(burnRate)}
          </div>
          <div
            className="text-[9px] text-slate-500"
            title={`Recurring ${formatEurCompact(Number(data.burn_recurring_eur))} + Steuer ${formatEurCompact(Number(data.burn_steuer_avg_eur))}`}
          >
            Recurring + Steuer-Ø
          </div>
        </div>
        <div className="rounded-lg border border-slate-700/60 bg-slate-900/40 p-2">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">
            Empfohlen (3 M)
          </div>
          <div className="text-base font-semibold text-slate-100 tabular-nums">
            {formatEurCompact(mindest)}
          </div>
          <div className={`text-[9px] tabular-nums ${coverageClass} ${flashClass(cashFlash)}`}>
            {cash === null
              ? "Cash: — (kein Banking)"
              : `aktuell ${formatEurCompact(cash)}`}
          </div>
        </div>
      </section>

      {/* Cash-Graph-Leiste: Deckungsgrad der 3-Monats-Reserve + Reichweite —
          dieselbe Zahl wie im LageHeader, hier als Balken. */}
      {deckung != null && (
        <section className="space-y-1">
          <div className="flex items-baseline justify-between text-[10px] tabular-nums">
            <span className="uppercase tracking-wider text-slate-500">
              Deckung 3-M-Reserve
            </span>
            <span className={coverageClass}>
              {Math.round(deckung)} %
              {reichweite != null && (
                <span className="text-slate-500">
                  {" "}
                  · Reichweite {reichweite} T
                </span>
              )}
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
            <div
              className={`h-full rounded-full ${
                coverage === "critical"
                  ? "bg-rose-400"
                  : coverage === "warn"
                    ? "bg-amber-400"
                    : "bg-emerald-400"
              }`}
              style={{
                width: `${Math.max(2, Math.min(100, deckung))}%`,
                transition: "width 700ms cubic-bezier(0.22,1,0.36,1)",
              }}
            />
          </div>
        </section>
      )}
    </div>
  );
}

function Skeleton() {
  return (
    <div className="h-full flex flex-col gap-3 animate-pulse">
      <div className="h-3 w-1/3 rounded bg-slate-700/50" />
      <div className="h-4 w-2/3 rounded bg-slate-700/40" />
      <div className="h-4 w-2/3 rounded bg-slate-700/40" />
      <div className="h-4 w-2/3 rounded bg-slate-700/40" />
      <div className="mt-auto grid grid-cols-2 gap-2">
        <div className="h-14 rounded bg-slate-700/20" />
        <div className="h-14 rounded bg-slate-700/20" />
      </div>
    </div>
  );
}
